import Vue from 'vue';
import { toTrackingStatus } from './tracking-statuses';

export { TRACKING_STATUS_MAP } from './tracking-statuses';

export const TRACKING_PROGRESS = {
  created: 5,
  awbIssued: 10,
  booked: 15,
  flightScheduled: 18,
  waitingForCargo: 20,
  cargoReceived: 30,
  cargoAccepted: 35,
  prepared: 40,
  loaded: 45,
  departed: 55,
  inTransit: 65,
  arrived: 75,
  atDestinationTerminal: 82,
  readyForPickup: 86,
  consigneeNotified: 90,
  delivered: 95,
  completed: 100,
  discrepancy: 65,
  flightCanceled: 15,
  cargoRejected: 25,
};

const TRANSPORT_STATUSES = [
  'cargoReceived',
  'cargoAccepted',
  'prepared',
  'loaded',
  'departed',
  'arrived',
  'atDestinationTerminal',
  'consigneeNotified',
  'delivered',
  'completed',
];

function normalizeLocale (locale) {
  return String(locale || 'ru').toLowerCase().startsWith('en') ? 'en' : 'ru';
}

function translate (path, params, locale) {
  const key = `cabinet.tracking.${path}`;
  const localize = Vue.prototype.$localize;
  if (!localize?.t) return key;
  return locale
    ? localize.t(key, normalizeLocale(locale), params)
    : localize.t(key, params);
}

function statusTitle (status, locale) {
  return translate(`statuses.${status}`, undefined, locale);
}

function addAirportToDescription (description, airport, locale) {
  if (!airport) return description;
  return [
    description,
    translate('descriptions.airport', { airport }, locale),
  ].filter(Boolean).join(' ');
}

export function getTrackingDateValue (value) {
  if (!value) return 0;
  if (typeof value.server === 'string' && value.server) {
    value = value.server;
  }
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }
  if (value.nativeDate instanceof Date) {
    const timestamp = value.nativeDate.getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
  );

  if (match) {
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
      Number(match[6] || 0)
    ).getTime();
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function flightNumber (flight) {
  if (!flight) return '';
  return flight.fullNumberNoSpace || `${flight.company || ''}${flight.number || ''}`;
}

function flightRoute (flight) {
  if (!flight) return '';
  return [flight.departure?.airport, flight.arrival?.airport].filter(Boolean).join(' → ');
}

function isSameFlight (flight, movementFlight) {
  if (!flight || !movementFlight) return false;
  if (flight.company && movementFlight.company && flight.company !== movementFlight.company) {
    return false;
  }
  if (flight.number && movementFlight.number && flight.number !== movementFlight.number) {
    return false;
  }
  if (
    flight.departure?.airport &&
    movementFlight.departure?.airport &&
    flight.departure.airport !== movementFlight.departure.airport
  ) {
    return false;
  }

  const flightTimestamp = getTrackingDateValue(flight.departure?.date);
  const movementTimestamp = getTrackingDateValue(movementFlight.departure?.date);
  const flightDay = flightTimestamp
    ? new Date(flightTimestamp).toISOString().slice(0, 10)
    : '';
  const movementDay = movementTimestamp
    ? new Date(movementTimestamp).toISOString().slice(0, 10)
    : '';
  return !flightDay || !movementDay || flightDay === movementDay;
}

function describeEvent (status, context) {
  const { awb, flight, locale, location, route } = context;
  const number = flightNumber(flight);
  const direction = flightRoute(flight) || route;

  if (status === 'awbIssued') {
    return addAirportToDescription(
      translate('descriptions.awbIssued', { awb }, locale),
      location,
      locale
    );
  }
  if (status === 'booked') {
    if (context.flights.length === 1) {
      return translate(
        'descriptions.bookedSingle',
        {
          flight: number,
          route: direction,
        },
        locale
      );
    }
    return translate('descriptions.bookedMultiple', { route }, locale);
  }
  if (status === 'flightScheduled') {
    return addAirportToDescription(
      [number, direction].filter(Boolean).join(' · '),
      location,
      locale
    );
  }
  if (status === 'prepared' && number) {
    return addAirportToDescription(
      translate('descriptions.preparedWithFlight', { flight: number }, locale),
      location,
      locale
    );
  }
  if (status === 'loaded' && number) {
    return addAirportToDescription(
      translate('descriptions.loadedWithFlight', { flight: number }, locale),
      location,
      locale
    );
  }
  if (status === 'departed') {
    const airport = location || flight?.departure?.airport;
    return airport
      ? translate('descriptions.departedWithAirport', {
        flight: number,
        airport,
      }, locale)
      : translate('descriptions.departed', { flight: number }, locale);
  }
  if (status === 'arrived') {
    const airport = location || flight?.arrival?.airport;
    return airport
      ? translate('descriptions.arrivedWithAirport', {
        flight: number,
        airport,
      }, locale)
      : translate('descriptions.arrived', { flight: number }, locale);
  }

  return addAirportToDescription(
    translate(`descriptions.${status}`, undefined, locale),
    location,
    locale
  );
}

function isDuplicateEvent (events, candidate) {
  return events.some(event => {
    if (event.status !== candidate.status) return false;
    if (flightNumber(event.flight) !== flightNumber(candidate.flight)) return false;
    if ((event.location || '') !== (candidate.location || '')) return false;
    if (
      (event.movement?.cargoPartsCode || '') !==
      (candidate.movement?.cargoPartsCode || '')
    ) {
      return false;
    }
    return Math.abs(
      getTrackingDateValue(event.datetime) - getTrackingDateValue(candidate.datetime)
    ) <= 5 * 60 * 1000;
  });
}

function latestDate (events) {
  return events.reduce((latest, event) => {
    return getTrackingDateValue(event.datetime) > getTrackingDateValue(latest)
      ? event.datetime
      : latest;
  }, '');
}

function lastEventByStatus (events, statuses) {
  return events
    .filter(event => statuses.includes(event.status))
    .sort((a, b) => (
      getTrackingDateValue(a.datetime) - getTrackingDateValue(b.datetime) ||
      (TRACKING_PROGRESS[a.status] || 0) - (TRACKING_PROGRESS[b.status] || 0)
    ))
    .pop();
}

function compareTimelineEvents (a, b) {
  const aDate = getTrackingDateValue(a.datetime || a.estimatedDatetime);
  const bDate = getTrackingDateValue(b.datetime || b.estimatedDatetime);
  if (!aDate && bDate) return 1;
  if (aDate && !bDate) return -1;
  if (aDate !== bDate) return aDate - bDate;
  return (TRACKING_PROGRESS[a.status] || 0) - (TRACKING_PROGRESS[b.status] || 0);
}

function getCurrentTrackingStatus (events, activeFlights) {
  const eventStatuses = new Set(events.map(event => event.status));
  const terminalPriority = [
    'completed',
    'delivered',
    'consigneeNotified',
    'atDestinationTerminal',
  ];
  const terminalStatus = terminalPriority.find(status => eventStatuses.has(status));
  if (terminalStatus) return terminalStatus;

  const latestTransport = lastEventByStatus(events, TRANSPORT_STATUSES);
  const latestRejected = lastEventByStatus(events, ['cargoRejected']);
  if (
    latestRejected &&
    getTrackingDateValue(latestRejected.datetime) >=
      getTrackingDateValue(latestTransport?.datetime)
  ) {
    return 'cargoRejected';
  }

  if (latestTransport?.status === 'departed') {
    const arrivedLater = events.some(event => {
      return event.status === 'arrived' &&
        isSameFlight(event.flight, latestTransport.flight) &&
        getTrackingDateValue(event.datetime) >= getTrackingDateValue(latestTransport.datetime);
    });
    return arrivedLater ? 'arrived' : 'inTransit';
  }
  if (latestTransport) return latestTransport.status;

  if (activeFlights.length) return 'waitingForCargo';
  if (eventStatuses.has('flightCanceled')) return 'flightCanceled';
  if (eventStatuses.has('booked')) return 'booked';
  if (eventStatuses.has('awbIssued')) return 'awbIssued';
  return eventStatuses.has('created') ? 'created' : 'created';
}

function futureEvent (status, locale, data = {}) {
  const isFlightStatus = ['departed', 'arrived'].includes(status);
  const flightDescription = isFlightStatus
    ? [flightNumber(data.flight), flightRoute(data.flight)].filter(Boolean).join(' · ')
    : '';
  let description = data.description;
  if (description === undefined) {
    description = isFlightStatus
      ? flightDescription
      : translate(`descriptions.${status}`, undefined, locale);
  }
  description = addAirportToDescription(description, data.location, locale);

  return {
    id: `future-${status}-${data.flight?.id || data.flight?.pageTitle || ''}`,
    status,
    title: translate(`future.${status}`, undefined, locale),
    description,
    state: 'future',
    estimatedDatetime: data.estimatedDatetime || '',
    flight: data.flight || null,
  };
}

function hasFlightEvent (events, status, flight) {
  return events.some(event => (
    event.status === status &&
    event.flight &&
    isSameFlight(flight, event.flight)
  ));
}

function buildFutureTimeline (currentStatus, activeFlights, events, locale, route) {
  const byCurrentStatus = {
    created: ['cargoAccepted', 'departed', 'arrived'],
    awbIssued: ['cargoAccepted', 'departed', 'arrived'],
    booked: ['cargoAccepted', 'departed', 'arrived'],
    waitingForCargo: ['cargoAccepted', 'departed', 'arrived'],
    cargoReceived: ['cargoAccepted', 'departed', 'arrived'],
    cargoAccepted: ['departed', 'arrived', 'atDestinationTerminal'],
    prepared: ['departed', 'arrived', 'atDestinationTerminal'],
    loaded: ['departed', 'arrived', 'atDestinationTerminal'],
    departed: ['arrived', 'atDestinationTerminal', 'consigneeNotified'],
    inTransit: ['arrived', 'atDestinationTerminal', 'consigneeNotified'],
    arrived: ['atDestinationTerminal', 'consigneeNotified', 'delivered'],
    atDestinationTerminal: ['consigneeNotified', 'delivered'],
    readyForPickup: ['consigneeNotified', 'delivered'],
    consigneeNotified: ['delivered'],
  };

  const statuses = byCurrentStatus[currentStatus] || [];
  const future = [];

  const cargoWasAccepted = events.some(event => event.status === 'cargoAccepted');
  if (statuses.includes('cargoAccepted') && !cargoWasAccepted) {
    future.push(futureEvent('cargoAccepted', locale, { location: route.departure }));
  }

  const flights = activeFlights.filter(flight => !flight.isCanceled);
  const canHaveFutureFlights = ![
    'atDestinationTerminal',
    'readyForPickup',
    'consigneeNotified',
    'delivered',
    'completed',
    'cargoRejected',
    'flightCanceled',
  ].includes(currentStatus);

  if (canHaveFutureFlights && flights.length) {
    flights.forEach((flight) => {
      if (!hasFlightEvent(events, 'departed', flight)) {
        future.push(futureEvent('departed', locale, {
          estimatedDatetime: flight.departure?.estimatedDate || flight.departure?.date,
          flight,
        }));
      }

      if (!hasFlightEvent(events, 'arrived', flight)) {
        future.push(futureEvent('arrived', locale, {
          estimatedDatetime: flight.arrival?.estimatedDate || flight.arrival?.date,
          flight,
        }));
      }
    });
  } else if (canHaveFutureFlights && !flights.length) {
    statuses
      .filter(status => ['departed', 'arrived'].includes(status))
      .forEach(status => future.push(futureEvent(status, locale)));
  }

  statuses
    .filter(status => !['cargoAccepted', 'departed', 'arrived'].includes(status))
    .forEach(status => future.push(futureEvent(status, locale, { location: route.arrival })));

  return future;
}

export function calculateTrackingProgress (timeline, currentStatus) {
  if (['cargoRejected', 'flightCanceled'].includes(currentStatus)) {
    return TRACKING_PROGRESS[currentStatus] || 0;
  }

  const countableEvents = timeline.filter(event => (
    ['completed', 'current', 'future', 'warning'].includes(event.state)
  ));

  if (!countableEvents.length) {
    return TRACKING_PROGRESS[currentStatus] || 0;
  }

  const reachedEvents = countableEvents.filter(event => event.state !== 'future');

  return Math.round((reachedEvents.length / countableEvents.length) * 100);
}

function buildRoutePath (shipment, flights) {
  const path = [];

  shipment.route.forEach(segment => {
    if (segment.departure && !path.length) path.push(segment.departure);
    if (segment.arrival && path[path.length - 1] !== segment.arrival) {
      path.push(segment.arrival);
    }
  });

  if (!path.length) {
    flights
      .filter(flight => !flight.isCanceled)
      .forEach(flight => {
        if (flight.departure?.airport && !path.length) path.push(flight.departure.airport);
        if (flight.arrival?.airport && path[path.length - 1] !== flight.arrival.airport) {
          path.push(flight.arrival.airport);
        }
      });
  }

  if (!path.length) {
    [shipment.departure, shipment.arrival].forEach(airport => {
      if (airport && path[path.length - 1] !== airport) {
        path.push(airport);
      }
    });
  }

  return path.join(' → ');
}

export function buildShipmentTracking (shipment, locale = Vue.prototype.$localize?.locale) {
  const normalizedLocale = normalizeLocale(locale);
  const awb = shipment.mawb;
  const bookings = shipment.sortedBookings;
  const flights = bookings.map(booking => booking.raid).filter(Boolean);
  const routePath = buildRoutePath(shipment, flights);
  const activeBookings = bookings.filter(booking => booking.isActive && !booking.raid?.isCanceled);
  const activeFlights = activeBookings.map(booking => booking.raid);
  const events = [];
  const alerts = [];

  const addEvent = data => {
    const event = {
      id: data.id,
      status: data.status,
      title: data.title || statusTitle(data.status, normalizedLocale),
      description: data.description === undefined
        ? describeEvent(data.status, {
          awb,
          flight: data.flight,
          flights: activeFlights,
          locale: normalizedLocale,
          location: data.location,
          route: routePath,
        })
        : data.description,
      state: data.state || 'completed',
      datetime: data.datetime || '',
      estimatedDatetime: data.estimatedDatetime || '',
      location: data.location || '',
      flight: data.flight || null,
      movement: data.movement || null,
      completion: data.completion || 'complete',
    };
    if (!isDuplicateEvent(events, event)) events.push(event);
  };

  if (shipment.createdAt) {
    addEvent({
      id: 'created',
      status: 'created',
      datetime: shipment.createdAt,
    });
  }

  const movements = shipment.movements
    .slice()
    .sort((a, b) => getTrackingDateValue(a.movedAt) - getTrackingDateValue(b.movedAt));
  const hasAwr = movements.some(movement => {
    return toTrackingStatus(movement.status) === 'awbIssued';
  });
  if (shipment.issuedAt && !hasAwr) {
    addEvent({
      id: 'awb-issued',
      status: 'awbIssued',
      datetime: shipment.issuedAt,
    });
  }

  if (flights.length) {
    const firstFlight = activeFlights[0] || flights[0];
    const bookedAt = activeBookings
      .map(booking => booking.createdAt)
      .filter(Boolean)
      .sort((a, b) => getTrackingDateValue(a) - getTrackingDateValue(b))[0] || '';
    addEvent({
      id: 'booked',
      status: 'booked',
      datetime: bookedAt,
      flight: firstFlight,
    });
  }

  bookings
    .filter(booking => booking.raid?.isCanceled)
    .forEach(booking => {
      const flight = booking.raid;
      const state = activeFlights.length ? 'warning' : 'error';
      const event = {
        id: `flight-canceled-${booking.id || flight.id}`,
        status: 'flightCanceled',
        title: statusTitle('flightCanceled', normalizedLocale),
        description: [flightNumber(flight), flightRoute(flight)].filter(Boolean).join(' · '),
        state,
        datetime: booking.disabledAt || '',
        estimatedDatetime: flight.departure?.date || '',
        flight,
      };
      addEvent(event);
      alerts.push({
        id: event.id,
        type: 'flightCanceled',
        severity: state,
        datetime: event.datetime,
        title: event.title,
        description: event.description,
      });
    });

  movements.forEach((movement, index) => {
    const status = toTrackingStatus(movement.status);
    if (!status || status === 'flightScheduled' || status === 'flightCanceled') return;
    const movementFlight = movement.raid;
    const matchingFlight = flights.find(flight => isSameFlight(flight, movementFlight));
    const flight = matchingFlight || movementFlight;
    const movementPieces = movement.cargo?.pieces;
    const totalPieces = shipment.cargo?.pieces;
    const completion = movementPieces && totalPieces && movementPieces < totalPieces
      ? 'partial'
      : 'complete';
    const state = status === 'discrepancy' ? 'warning' : 'completed';
    const event = {
      id: `movement-${status}-${index}`,
      status,
      datetime: movement.movedAt || '',
      location: movement.location || '',
      flight,
      movement,
      completion,
      state,
    };
    addEvent(event);

    if (status === 'discrepancy') {
      alerts.push({
        id: event.id,
        type: 'discrepancy',
        severity: 'warning',
        datetime: event.datetime,
        title: statusTitle('discrepancy', normalizedLocale),
        description: translate('descriptions.discrepancy', undefined, normalizedLocale),
      });
    }
  });

  if (shipment.acceptedAt) {
    addEvent({
      id: 'cargo-accepted',
      status: 'cargoAccepted',
      datetime: shipment.acceptedAt,
      location: shipment.departure,
    });
  }
  if (shipment.data.unacceptedAt) {
    const event = {
      id: 'cargo-rejected',
      status: 'cargoRejected',
      datetime: shipment.data.unacceptedAt,
      location: shipment.departure,
      state: 'error',
    };
    addEvent(event);
    alerts.push({
      id: event.id,
      type: 'cargoRejected',
      severity: 'error',
      datetime: event.datetime,
      title: statusTitle('cargoRejected', normalizedLocale),
      description: translate('descriptions.cargoRejected', undefined, normalizedLocale),
    });
  }

  const hasCompletedMovement = events.some(event => event.status === 'completed');
  if (shipment.deliveredAt && !hasCompletedMovement) {
    addEvent({
      id: 'completed',
      status: 'completed',
      datetime: shipment.deliveredAt,
      location: shipment.arrival,
    });
  }

  events.sort(compareTimelineEvents);

  let currentStatus = getCurrentTrackingStatus(events, activeFlights);
  const currentFlight = lastEventByStatus(events, ['departed'])?.flight ||
    activeFlights[0] || null;
  let currentEventIsOutdated = false;

  if (currentStatus === 'waitingForCargo') {
    addEvent({
      id: 'waiting-for-cargo',
      status: currentStatus,
      state: 'current',
      flight: currentFlight,
      location: shipment.departure,
    });
  } else if (currentStatus === 'inTransit') {
    addEvent({
      id: 'in-transit',
      status: currentStatus,
      state: 'current',
      flight: currentFlight,
      location: currentFlight?.arrival?.airport || shipment.arrival,
      estimatedDatetime: currentFlight?.arrival?.estimatedDate ||
        currentFlight?.arrival?.date || '',
    });
  } else if (!['completed', 'flightCanceled', 'cargoRejected'].includes(currentStatus)) {
    const currentEvent = events
      .filter(event => event.status === currentStatus && event.state === 'completed')
      .pop();
    currentEventIsOutdated = !!currentEvent &&
      currentStatus === 'atDestinationTerminal' &&
      events.some(event => (
        event !== currentEvent &&
        event.state === 'completed' &&
        getTrackingDateValue(event.datetime) > getTrackingDateValue(currentEvent?.datetime)
      ));
    if (currentEvent && !currentEventIsOutdated) currentEvent.state = 'current';
  }

  events.sort((a, b) => {
    if (a.state === 'current' && !a.datetime) return 1;
    if (b.state === 'current' && !b.datetime) return -1;
    return compareTimelineEvents(a, b);
  });

  const futureTimeline = buildFutureTimeline(
    currentStatus,
    activeFlights,
    events,
    normalizedLocale,
    {
      departure: shipment.departure,
      arrival: shipment.arrival,
    }
  );

  if (currentEventIsOutdated && futureTimeline.length) {
    futureTimeline[0].state = 'current';
    currentStatus = futureTimeline[0].status;
  }

  const timeline = [...events, ...futureTimeline];

  return {
    status: currentStatus,
    updatedAt: latestDate(events.filter(event => event.datetime)),
    timeline,
    alerts,
  };
}
